import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { injectIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { Loader, Modal } from 'semantic-ui-react';

import { setAgreementClosed, setAgreementOpen } from '../../actions/AgreementsActions';
import { StateType } from '../../reducers';
import { agreementsIsFetching, isAgreementOpen } from '../../reducers/AgreementsReducer/AgreementsSelector';
import { DispatchConnect } from '../../types';

import './Agreements.scss';
import { useForm } from 'react-hook-form';
import { UserAgreementsMain } from './UserAgreementsMain';
import { useAgreement } from '../../shared/api/apiHooks';
import { competitionNameSelector, logoutCompetitionIdSelector } from '../../reducers/CompetitionsReducer/CompetitionsSelector';
import * as Fields from '../Account/Data/Fields';
import {
  UserAgreement,
  UserAgreementsContext,
  UserAgreementsPropsGeneric,
  UserAgreementsStateProps,
  UserAgreementsStateStateDispatchers,
} from './UserAgreementsTypes';

import * as wizardSelectors from '../../reducers/AccountReducer/AccountWizardSelector';
import { agreementFormStageSelector } from '../../reducers/AccountReducer/AccountWizardSelector';
import Immutable, { mergeDeep } from 'immutable';
import animateScrollTo from 'animated-scroll-to';
import { AGREEMENTS_FORM_STAGES } from '../../reducers/AccountReducer/InitialAccountWizardData';
import { AppDispatch, GetState } from '../../shared/services/store';
import { setAgreementsFormStage } from '../../actions/AccountActions/AccountWizardActions';
import { defaultErrorMessage } from '../../shared/helpers/fetch-helper';
import { CAPTCHA_RETRY_SYMBOL } from '../../reducers/CaptchaReducer/CaptchaActions';
import { logErrorService } from '../../shared/services';
import { useAppDispatch } from '../../shared/services/store/useAppDispatch';

export const USER_AGREEMENTS_ID = 'user-agreement';

const UserAgreements = (props: UserAgreementsPropsGeneric) => {
  const { isShowCompetitionAgreement = true, isShowAgree = true, isModalOpen = false } = props;

  const logoutCompetition = useSelector(logoutCompetitionIdSelector) as any;
  const competitionName = useSelector(competitionNameSelector) as string | undefined;

  const userAgreementQuery = useAgreement({ scope: 'public', agreementId: USER_AGREEMENTS_ID });
  const termsQuery = useAgreement({ scope: 'public', agreementId: 'terms-conditions' });
  const challengeQuery = useAgreement({ scope: 'competition', competitionId: 'challenge' });

  const competitionAgreementQuery = useAgreement({
    scope: 'competition',
    competitionId: logoutCompetition as string,
    enabled: !!(logoutCompetition && competitionName),
  });

  const dispatch = useAppDispatch();

  const agreementsList = useMemo(() => {
    const result: UserAgreement[] = [
      {
        label: 'terms.iHaveCarefullyRead',
        value: false,
        name: 'terms_understand',

        validate: ['isTrue'],
      },
      {
        label: 'terms.asProvided',
        value: false,
        name: 'terms_compensation',

        validate: ['isTrue'],
      },
      {
        label: 'terms.iHaveFullRight',
        value: false,
        name: 'terms_authority',

        validate: ['isTrue'],
      },
    ];

    if (isShowAgree) {
      result.push({
        label: 'agreements.termsConditions',
        value: false,
        name: Fields.agreeTermsConditions.props.name,
        validate: ['isTrue'],
      });
    }

    if (logoutCompetition && competitionName && competitionAgreementQuery.isSuccess && isShowCompetitionAgreement) {
      result.push({
        label: 'agreements.competition',
        labelValues: {
          competition: competitionName,
          agreement: competitionAgreementQuery.data.title,
        },
        value: true,
        name: Fields.agreeCompetition.props.name,
        validate: [],
      });
    }

    return result;
  }, [logoutCompetition, competitionName, competitionAgreementQuery.isSuccess, isShowCompetitionAgreement, isShowAgree]);
  const dataFields = useSelector(wizardSelectors.dataFieldsSelector) as Immutable.Map<any, any>;

  const setInitialAgreementsListValues = useCallback(() => {
    const agreementsListValues: any = {};
    agreementsList.forEach((agreement: UserAgreement) => {
      agreementsListValues[agreement.name] = agreement.value;
    });

    return mergeDeep({}, agreementsListValues, dataFields.toJS()) as any;
  }, [agreementsList, dataFields]);

  const handleCloseModal = () => {
    props?.handleClose?.();
    props.dispatchCloseAgreement(USER_AGREEMENTS_ID);
  };

  const agreementsForm = useForm({
    defaultValues: setInitialAgreementsListValues(),
    mode: 'all',
    criteriaMode: 'all',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (competitionAgreementQuery.isSuccess) {
      agreementsForm.reset(setInitialAgreementsListValues(), { keepValues: true, keepErrors: true });
    }
  }, [competitionAgreementQuery.isSuccess, agreementsForm, setInitialAgreementsListValues]);

  const handleAcceptInternal = (data: any) => async (dispatch: AppDispatch, getState: GetState) => {
    try {
      const formStage = agreementFormStageSelector(getState());
      if (formStage === AGREEMENTS_FORM_STAGES.ReadAgreement) {
        dispatch(setAgreementsFormStage(AGREEMENTS_FORM_STAGES.SubmitAgreement));
        return;
      }

      if (formStage === AGREEMENTS_FORM_STAGES.SubmitAgreement) {
        const result = await props.handleAccepted?.(data);
        if (result === CAPTCHA_RETRY_SYMBOL) {
          // This block causes nothing happen when accepting the agreements
          logErrorService('user accepted agreements but met CAPTCHA_RETRY_SYMBOL');
          return;
        }
        handleCloseModal();

        return;
      }
    } catch (e) {
      defaultErrorMessage(dispatch);
    }
  };

  const handleBack = () => (dispatch: AppDispatch, getState: GetState) => {
    const formStage = agreementFormStageSelector(getState());

    if (formStage === AGREEMENTS_FORM_STAGES.ReadAgreement) {
      handleCloseModal();
      return;
    }

    if (formStage === AGREEMENTS_FORM_STAGES.SubmitAgreement) {
      dispatch(setAgreementsFormStage(AGREEMENTS_FORM_STAGES.ReadAgreement));
      return;
    }

    throw new Error('Unknown User Agreement form stage');
  };

  const handleError = (errors: any) => {
    const fieldElement = document.querySelector('.agreement__field-error');
    if (fieldElement) {
      animateScrollTo(fieldElement, { element: document.querySelector('.agreements__form-content'), maxDuration: 0, verticalOffset: 16 });
    }
  };

  const agreementsContext: UserAgreementsContext = {
    modifiers: ['user-agreement'],
    handleAccepted: agreementsForm.handleSubmit((data: any) => dispatch(handleAcceptInternal(data)), handleError),
    handleBack: () => dispatch(handleBack()),
    agreementsList: agreementsList,
    control: agreementsForm.control,
    form: agreementsForm,
    userAgreementQuery,
    termsQuery,
    challengeQuery,
    competitionAgreementQuery,
    logoutCompetition,
    competitionName,
    isShowCompetitionAgreement,
    isShowAgree,
  };

  return (
    <Modal
      size="large"
      closeOnDimmerClick={false}
      closeOnEscape={false}
      open={isModalOpen}
      onClose={handleCloseModal}
      className={'user-agreements'}
    >
      <Modal.Content>
        {props.isFetching ? (
          <div className="loader">
            <Loader active={true} inline="centered" size="massive" />
          </div>
        ) : (
          <UserAgreementsMain context={agreementsContext} />
        )}
      </Modal.Content>
    </Modal>
  );
};

const mapStateToProps = (state: StateType, ownProps: UserAgreementsPropsGeneric): UserAgreementsStateProps => ({
  agreements: state.getIn(['agreements', 'agreements', USER_AGREEMENTS_ID]),
  // @ts-expect-error agreementsIsFetching returns unknown, not boolean
  isFetching: agreementsIsFetching(state),
  isModalOpen: isAgreementOpen(state, USER_AGREEMENTS_ID),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): UserAgreementsStateStateDispatchers => ({
  dispatchOpenAgreement: (id: string) => {
    dispatch(setAgreementOpen({ id }));
  },
  dispatchCloseAgreement: (id: string) => {
    dispatch(setAgreementClosed({ id }));
  },
});

export default connect<UserAgreementsStateProps, DispatchConnect<UserAgreementsStateStateDispatchers>, any, StateType>(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(UserAgreements));
